import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, Info, MapPin, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LocalCleaningSection } from "@/components/local-cleaning-section";
import { directoryRegions } from "@/data/directory-pages";
import { cityGeo, cityPages, getCityPage } from "@/data/city-pages";
import { getTradePage, tradePages } from "@/data/trade-pages";
import { breadcrumbLd, geoMeta, jsonLdScript, priceOffersLd, SITE_URL } from "@/lib/json-ld";

export const Route = createFileRoute("/handwerker/$stadt_/$gewerk")({
  // Concrete city/trade URLs are expanded by sitemap.xml when this route is included.
  staticData: { sitemap: true },
  loader: ({ params }) => {
    const city = getCityPage(params.stadt);
    const trade = getTradePage(params.gewerk);
    if (!city || !trade) throw notFound();
    return { city, trade };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [{ title: "Seite nicht gefunden | Begner" }, { name: "robots", content: "noindex" }],
      };
    }
    const { city, trade } = loaderData;
    const path = `/handwerker/${city.slug}/${trade.slug}`;
    const isCleaning = trade.slug === "reinigung";
    const title = isCleaning
      ? `Reinigungsfirma ${city.name}: Reinigung & Umzug | Begner`
      : `${trade.name} in ${city.name}: Offerten vergleichen | Begner`;
    const description = isCleaning
      ? `Reinigungsfirmen in ${city.name}, Kanton ${city.cantonName}: Baureinigung, Bauendreinigung und Umzugsreinigung. Leistungen und Kosten vergleichen, Offerten anfragen.`
      : `${trade.name} in ${city.name} und im Kanton ${city.cantonName} finden. Richtpreise, Tipps und kostenlose Offerten von geprüften Betrieben.`;
    const geo = cityGeo[city.slug];
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: `${SITE_URL}${path}` },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        ...(geo ? geoMeta(geo.lat, geo.lng, `CH-${city.canton.toUpperCase()}`, city.name) : []),
      ],
      scripts: [
        jsonLdScript({
          "@context": "https://schema.org",
          "@type": "Service",
          name: `${trade.name} in ${city.name}`,
          serviceType: trade.name,
          url: `${SITE_URL}${path}`,
          areaServed: {
            "@type": "City",
            name: city.name,
            containedInPlace: { "@type": "AdministrativeArea", name: `Kanton ${city.cantonName}` },
            ...(geo
              ? { geo: { "@type": "GeoCoordinates", latitude: geo.lat, longitude: geo.lng } }
              : {}),
          },
          provider: { "@type": "Organization", name: "Begner", url: SITE_URL },
          offers: priceOffersLd(trade.prices),
        }),
        jsonLdScript(
          breadcrumbLd([
            { name: "Startseite", path: "/" },
            { name: city.name, path: `/handwerker/${city.slug}` },
            { name: trade.name, path },
          ]),
        ),
      ],
    };
  },
  notFoundComponent: () => (
    <div className="container-page py-20 text-center">
      <h1 className="font-display text-3xl font-bold">Seite nicht gefunden</h1>
      <Button asChild className="mt-6 rounded-xl">
        <Link to="/auftragnehmer">Alle Auftragnehmer</Link>
      </Button>
    </div>
  ),
  component: CityTradeView,
});

function CityTradeView() {
  const { city, trade } = Route.useLoaderData();
  const isCleaning = trade.slug === "reinigung";
  const directoryRegion = directoryRegions.find((region) => region.canton === city.canton)!;
  const firstPrice = trade.prices[0];
  const nearby = cityPages.filter((c) => city.nearby.includes(c.slug));
  const otherTrades = tradePages.filter((t) => t.slug !== trade.slug);

  return (
    <>
      <section className="border-b border-border bg-surface py-14 md:py-20">
        <div className="container-page max-w-4xl">
          <p className="inline-flex items-center gap-2 text-sm font-semibold text-brand-deep">
            <MapPin className="h-4 w-4" /> {city.name}, Kanton {city.cantonName}
          </p>
          <h1 className="mt-3 font-display text-4xl font-bold leading-tight md:text-5xl">
            {isCleaning ? "Reinigungsfirmen und Umzugsfirmen" : trade.name} in {city.name} finden
          </h1>
          <p className="mt-5 text-lg leading-8 text-muted-foreground">
            {isCleaning
              ? `Ob Wohnungsreinigung, Endreinigung mit Abnahmegarantie oder Umzug: Beschreiben Sie Ihren Auftrag kostenlos und vergleichen Sie Offerten von Reinigungsfirmen und Umzugsfirmen aus ${city.name} und Umgebung.`
              : `Beschreiben Sie Ihr Projekt einmal kostenlos und vergleichen Sie Offerten von Betrieben für ${trade.name} aus ${city.name} und Umgebung.`}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="rounded-xl">
              <Link to="/auftrag-einstellen">
                Offerten kostenlos anfragen <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-xl bg-card">
              <Link to="/handwerkerverzeichnis/$region" params={{ region: directoryRegion.slug }}>
                Betriebe im Kanton {city.cantonName}
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container-page max-w-4xl">
          <div className="rounded-2xl border border-brand/30 bg-brand-soft p-6">
            <p className="inline-flex items-center gap-2 text-sm font-semibold text-brand-deep">
              <Info className="h-4 w-4" /> Kurz erklärt
            </p>
            <p className="mt-3 leading-7">
              {trade.name} in {city.name}: Auf Begner beschreiben Sie Ihr Projekt kostenlos und
              erhalten Offerten von Betrieben aus dem Kanton {city.cantonName}.
              {firstPrice
                ? ` Richtwert: ${firstPrice.label} ${firstPrice.range} (inkl. MwSt.).`
                : ""}
            </p>
          </div>
        </div>
      </section>

      <section className="pb-14">
        <div className="container-page grid max-w-4xl gap-10 md:grid-cols-2">
          <div>
            <h2 className="font-display text-2xl font-bold">Typische Arbeiten in {city.name}</h2>
            <ul className="mt-5 space-y-3">
              {trade.services.map((service) => (
                <li key={service} className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-brand" />
                  {service}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm leading-6 text-muted-foreground">{city.context}</p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
            <h2 className="font-display text-2xl font-bold">Richtpreise</h2>
            <dl className="mt-5 divide-y divide-border">
              {trade.prices.map((price) => (
                <div key={price.label} className="flex justify-between gap-4 py-3 text-sm">
                  <dt className="text-muted-foreground">{price.label}</dt>
                  <dd className="shrink-0 font-semibold">{price.range}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-4 text-xs text-muted-foreground">
              Unverbindliche Schweizer Richtwerte inkl. MwSt. Preise in {city.name} können je nach
              Projekt abweichen.
            </p>
          </div>
        </div>
      </section>

      {isCleaning && <LocalCleaningSection location={city.name} cantonName={city.cantonName} />}

      <section className="border-t border-border bg-surface py-12">
        <div className="container-page max-w-4xl space-y-8">
          <div>
            <h2 className="font-display text-xl font-bold">{trade.name} in der Nähe</h2>
            <div className="mt-5 flex flex-wrap gap-3">
              {nearby.map((c) => (
                <Link
                  key={c.slug}
                  to="/handwerker/$stadt/$gewerk"
                  params={{ stadt: c.slug, gewerk: trade.slug }}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium hover:border-brand hover:text-brand-deep"
                >
                  <MapPin className="h-4 w-4 text-brand" /> {trade.name} {c.name}
                </Link>
              ))}
              <Link
                to="/gewerke/$gewerk"
                params={{ gewerk: trade.slug }}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium hover:border-brand hover:text-brand-deep"
              >
                <Wrench className="h-4 w-4 text-brand" /> {trade.name} Schweiz
              </Link>
            </div>
          </div>
          <div>
            <h2 className="font-display text-xl font-bold">Weitere Handwerker in {city.name}</h2>
            <div className="mt-5 flex flex-wrap gap-3">
              {otherTrades.map((t) => (
                <Link
                  key={t.slug}
                  to="/handwerker/$stadt/$gewerk"
                  params={{ stadt: city.slug, gewerk: t.slug }}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium hover:border-brand hover:text-brand-deep"
                >
                  <Wrench className="h-4 w-4 text-brand" /> {t.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
